import dayjs from "dayjs";

import {
  ChannelkartNetworkGet,
  ChannelkartNetworkPost,
  ChannelkartNetworkPut,
} from "../../../services/networkService";
import { getOutletDetails, getOutletDetailsForOutletCodeList } from "../../createSecondaryOrder/CreateSecondaryOrderService";
import {
  CreditNoteData,
  CreditNoteDetails,
  InvoiceData,
  Payload,
  Payment,
  PaymentCollectionRow,
  PaymentSummary,
  PreparePayloadParams,
} from "../collectionUtils";
import { truncateToTwoDecimals } from "../../../components/dms_common/DmsUtils";
import { getUTCDate } from "../../../services/manageOrderService";

export const fetchAllData = async (outletCode?: string, creditStatus?: string) => {
  try {
    const loginId = JSON.parse(localStorage.authContext)?.user?.loginId;

    // Step 1: Fetch initial outlet details
    const api1Response = await getOutletDetails(outletCode);
    let outletData = Array.isArray(api1Response) ? api1Response : [];

    // --- Fetch and add inactive outlet data ---
    try {
      // First, get the list of inactive outlet metadata
      const inactiveMetadataResponse = await ChannelkartNetworkGet(
        `/outletMetadata?filter=supplierCode%3A${loginId}%20and%20activeStatus%3Ainactive&size=1000`
      );

      const inactiveFeatures = inactiveMetadataResponse?.data?.features;

      if (Array.isArray(inactiveFeatures) && inactiveFeatures.length > 0) {
        // Extract unique outlet codes from the metadata
        const uniqueInactiveOutletCodes = Array.from(
          new Set(
            inactiveFeatures
              .map((item: any) => item?.outletCode)
              .filter((code: any) => code != null && code !== "")
          )
        );

        if (uniqueInactiveOutletCodes.length > 0) {
          // Second, fetch the full details for these inactive outlets
          const inactiveOutletDetails = await getOutletDetailsForOutletCodeList(uniqueInactiveOutletCodes);

          if (Array.isArray(inactiveOutletDetails) && inactiveOutletDetails.length > 0) {
            // Add the fetched inactive outlet details to the main outletData array
            outletData = [...outletData, ...inactiveOutletDetails];
          }
        }
      }
    } catch (error) {
        console.error("Error fetching inactive outlet data:", error);
        // We can continue without this data if it fails
    }
    // --- End inactive outlet data fetch ---

    // Step 2: Fetch credit status with pagination
    const size = 1000;
    let page = 0;
    let creditData: any[] = [];
    let hasMoreData = true;
    
    // Fetch credit status data in a loop with pagination
    while (hasMoreData) {
      let creditStatusFilter = creditStatus ? `creditStatus=${encodeURIComponent(creditStatus)}&` : '';
      const api2Response = await ChannelkartNetworkGet(
        `/creditStatus/getCreditStatusByUser${outletCode ? `?outletCode=${outletCode}&` : '?'}${creditStatusFilter}page=${page}&size=${size}`
      );

      // Extract data from response
      const pageData = Array.isArray(api2Response?.data?.content)
        ? api2Response.data.content
        : [];

      // Append to creditData
      creditData = [...creditData, ...pageData];

      // Check if there's more data to fetch
      hasMoreData = pageData.length === size;
      page += 1;
    }

    // Early return if no credit data is available
    if (!creditData.length) {
      console.log("No credit data available");
      return [];
    }

    // Step 3: Extract unique outlet codes from credit data with null checks
    const uniqueOutletCodes = Array.from(
      new Set(
        creditData
          .map((item: any) => item?.outletCode)
          .filter((code: any) => code != null && code !== "") // Filter out null/undefined/empty values
      )
    );

    // Step 4: Fetch credit note data once without outletCodes
    let amountData: any[] = [];
    try {
      const api3Response = await ChannelkartNetworkGet(
        `/creditNote/getAvailableCreditByOutlets?supplier=${loginId}`
      );

      amountData = Array.isArray(api3Response?.data) ? api3Response?.data : [];
    } catch (error) {
      console.error("Error fetching amount data:", error);
      amountData = [];
    }

    // Step 5: Create efficient lookup maps with enhanced null checks
    const outletMap = outletData.reduce((acc: any, outlet: any) => {
      if (outlet?.outletCode) {
        acc[outlet.outletCode] = {
          outletName: outlet?.outletName || "",
          preferredPaymentMode:
          Boolean(outlet?.extendedAttributes?.preferredPaymentMode) ? outlet?.extendedAttributes?.preferredPaymentMode : (outlet?.paymentMode|| ""),
          gstNo: Boolean(outlet?.gstNo),
        };
      }
      return acc;
    }, {});

    const amountMap = amountData.reduce((acc: any, item: any) => {
      if (item?.outletCode) {
        acc[item.outletCode] =
          typeof item.amount === "number" ? item.amount : 0;
      }
      return acc;
    }, {});

    // Step 6: Combine all data using credit data as the base with comprehensive null checks
    const result = creditData.map((creditItem: any, idx: number) => {
      const outletCode = creditItem?.outletCode || "";

      const outletInfo = outletMap[outletCode] || {
        outletName: "",
        preferredPaymentMode: "",
        gstNo: false,
      };

      const creditLimit =
        typeof creditItem?.creditLimit === "number"
          ? creditItem.creditLimit
          : 0;
      const creditAvailable =
        typeof creditItem?.creditAvailable === "number"
          ? creditItem.creditAvailable
          : 0;
      const creditAmount =
        typeof creditItem?.creditAmount === "number"
          ? creditItem.creditAmount
          : 0;
      const amountLastReceived =
        typeof creditItem?.amountLastReceived === "number"
          ? creditItem.amountLastReceived
          : 0;
      const totalDueDays =
        typeof creditItem?.totalDueDays === "number"
          ? creditItem.totalDueDays
          : 0;
      const creditNoteAmount =
        typeof amountMap[outletCode] === "number" ? amountMap[outletCode] : 0;

      return {
        id: creditItem?.outletCode ?? idx,
        outletCode: creditItem?.outletCode ?? "",
        loginId: creditItem?.loginId ?? "",
        referenceNumber: creditItem?.referenceNumber ?? "",
        creditStatus: creditItem?.creditStatus ?? "",
        status: creditItem?.creditStatus ?? "",
        creditLimit,
        creditAvailable,
        creditAmount,
        creditExpiry: creditItem?.creditExpiry ?? "",
        creditDueDate: creditItem?.creditDueDate ?? "",
        lastModifiedTime: creditItem?.lastModifiedTime ?? "",
        amountLastReceived,
        totalDueDays,
        outletName: outletInfo?.outletName || "",
        outletType: outletInfo?.preferredPaymentMode || "",
        gstApplicable: Boolean(outletInfo?.gstNo) || false,
        creditNoteAmount,
        balanceAmount: creditAvailable - creditNoteAmount,
        redirectIcon:
          creditItem?.creditStatus?.toLowerCase() === "paid" ? "eyeIcon" : "walletIcon",
      };
    });

    // Step 7: Sort result to prioritize "UNPAID" status
    result.sort((a, b) => {
      const statusA = a.status?.toUpperCase();
      const statusB = b.status?.toUpperCase();
      if (statusA === "UNPAID" && statusB !== "UNPAID") return -1;
      if (statusB === "UNPAID" && statusA !== "UNPAID") return 1;
      return b.creditAmount - a.creditAmount; // Sort by creditAmount in descending order
    });

    return result;
  } catch (error) {
    console.error("Error in fetchAllData:", error);
    throw error;
  }
};


export const preparePayload = ({
  paymentDetails,
  creditNotes,
  outletCode,
  type,
  selectedInvoice,
  useExpiredCreditNotes,
  totalAmount,
}: PreparePayloadParams & { useExpiredCreditNotes: boolean; totalAmount: number }): Payload => {
  const validPaymentDetails = Array.isArray(paymentDetails) ? paymentDetails : [];
  const validCreditNotes = Array.isArray(creditNotes) ? creditNotes : [];
  const validOutletCode = outletCode || "";
  const validType = type || "";

  validPaymentDetails.forEach((detail) => {
    if (detail && (detail.mode === "Cheque" || detail.mode === "NEFT")) {
      const ifsc = detail.ifscCode?.trim().toUpperCase() || "";
      if (!ifsc) throw new Error(`${detail.mode} IFSC code cannot be empty!`);
      if (ifsc.length !== 11) throw new Error(`${detail.mode} IFSC code must be exactly 11 characters long!`);
      const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
      if (!ifscRegex.test(ifsc)) {
        throw new Error(`${detail.mode} IFSC code must follow format: 4 letters (A–Z), '0', 6 alphanumeric (A–Z, 0–9)!`);
      }
    }
    if (detail && detail.mode === "UPI") {
      const txId = detail.transactionId?.trim().replace(/[^A-Za-z0-9]/g, '') || "";
      if (!txId) throw new Error("UPI Transaction ID cannot be empty!");
      if (txId.length < 12 || txId.length > 20) {
        throw new Error("UPI Transaction ID must be between 12 and 20 characters!");
      }
      if (!/^[A-Za-z0-9]+$/.test(txId)) {
        throw new Error("UPI Transaction ID must contain only alphanumeric characters!");
      }
    }
    if (detail && detail.mode === "NEFT") {
      if (
        !detail.amount ||
        detail.amount <= 0 ||
        !detail.payeeName ||
        !detail.payeeName.trim() ||
        !detail.bankName ||
        !detail.bankName.trim() ||
        !detail.bankAccountNo ||
        !detail.bankAccountNo.trim() ||
        !detail.ifscCode ||
        !detail.ifscCode.trim() ||
        !detail.transactionId ||
        !detail.transactionId.trim()
      ) {
        throw new Error("All NEFT fields are mandatory");
      }
    }
  });

  const paymentTotal = validPaymentDetails.reduce(
    (sum, detail) => sum + (typeof detail?.amount === "number" ? detail.amount : 0),
    0
  );

  const availableCreditNotes = validCreditNotes.filter(
    (note) => useExpiredCreditNotes || !note.expired
  );
  availableCreditNotes.sort((a, b) => {
    const dateA = new Date(a.endDate);
    const dateB = new Date(b.endDate);
    if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
      console.warn("Invalid endDate encountered:", { a, b });
      return 0;
    }
    return dateA.getTime() - dateB.getTime();
  });

  let remaining = totalAmount - paymentTotal;
  let selectedCreditNotes = [];
  if (remaining > 0) {
    let sum = 0;
    for (const note of availableCreditNotes) {
      if (sum >= remaining) break;
      const useAmount = Math.min(note.availableAmount, remaining - sum);
      if (useAmount > 0) {
        selectedCreditNotes.push({ ...note, usedAmount: useAmount });
        sum += useAmount;
      }
    }
  }

  const creditNotesTotal = selectedCreditNotes.reduce(
    (sum, note) => sum + note.usedAmount,
    0
  );
  const totalAmountPayload = truncateToTwoDecimals(paymentTotal + creditNotesTotal);

  const paymentObjects: Payment[] = validPaymentDetails
    .filter((detail) => detail && detail.mode)
    .map((detail) => {
      const payment: Payment = {
        mode: (detail.mode || "").toUpperCase(),
        amount: typeof detail.amount === "number" ? detail.amount : 0,
      };
      if (detail.mode === "UPI") {
        payment.transactionId = detail.transactionId?.trim().replace(/[^A-Za-z0-9]/g, '') || "";
      } else if (detail.mode === "Cheque") {
        payment.chequeNo = detail.bankAccountNo || "";
        payment.chequeDate = detail.chequeDate || "";
        payment.bankName = detail.bankName || "";
        payment.ifscCode = detail.ifscCode?.trim().toUpperCase() || "";
      } else if (detail.mode === "NEFT") {
        payment.bankAccountNumber = detail.bankAccountNo || "";
        payment.payeeName = detail.payeeName || "";
        payment.bankName = detail.bankName || "";
        payment.ifscCode = detail.ifscCode?.trim().toUpperCase() || "";
        payment.transactionId = detail.transactionId || "";
      }
      return payment;
    });

  const creditNotePayments: Payment[] = selectedCreditNotes.map((note) => ({
    mode: "CREDIT_NOTE",
    creditNoteNumber: note.noteNumber,
    amount: note.usedAmount,
  }));

  const allPayments = [...paymentObjects, ...creditNotePayments];
  const loginId = JSON.parse(localStorage.authContext)?.user?.loginId;

  const payload: Payload = {
    outletCode: validOutletCode,
    typeOfCustomer: validType,
    totalAmount: totalAmountPayload,
    payments: allPayments,
    status: "APPROVED",
    supplier: loginId,
  };

  if (validType === "cash") {
    if (!selectedInvoice || !selectedInvoice.id) {
      throw new Error("Invoice must be selected for cash outlet");
    }
    payload.invoiceNumber = selectedInvoice.id;
  }

  return payload;
};

export const handleSubmit = async (payload: Payload): Promise<unknown> => {
  try {
    // Validate payload before submission
    if (!payload || typeof payload !== "object") {
      throw new Error("Invalid payload provided");
    }

    const response = await ChannelkartNetworkPost(
      `/v1/paymentCollection/collectPayment`,
      payload
    );

    if (!response || response?.status !== 200) {
      throw new Error(
        `API request failed with status ${response?.status || "unknown"}`
      );
    }

    const result = await response?.status

    return result;
  } catch (error) {
    console.error("Error submitting payment:", error);
    throw error;
  }
};

export const fetchAndTransformPaymentCollectionData = async (
  outletCode: string,
  supplier: string,
  fromDate: string,
  toDate: string
): Promise<PaymentCollectionRow[]> => {
  try {
    const url = `/v1/paymentCollection/findBySupplierAndOutletCode?outletCode=${outletCode}&supplier=${supplier}&fromDate=${fromDate}&toDate=${toDate}`;
    const response = await ChannelkartNetworkGet(url);
  
    const data: any[] = Array.isArray(response?.data?.content)
      ? response?.data?.content
      : [];

    if (!data.length) {
      console.log("No payment collection data available");
      return [];
    }

    const groupedData = data.reduce((acc: { [key: string]: any[] }, item) => {
      const key = item.uniqueId;
      if (key) {
        acc[key] = acc[key] || [];
        acc[key].push(item);
      }
      return acc;
    }, {});

    const tableRows: PaymentCollectionRow[] = Object.keys(groupedData).map(
      (key, index) => {
        const group = groupedData[key];
        const firstItem = group[0];

        const sumCollectedAmount = group.reduce(
          (sum, item) => sum + (item.collectedAmount || 0),
          0
        );

        const invoices = Array.from(
          new Set(group.map((item) => item.invoiceNumber).filter(Boolean))
        );

        const creditNoteItems = group.filter(
          (item) => item.modeOfPayment?.toUpperCase() === "CREDIT_NOTE"
        );
        const nonCreditNoteItems = group.filter(
          (item) => item.modeOfPayment?.toUpperCase() !== "CREDIT_NOTE"
        );

        const creditNoteUsed = creditNoteItems.length > 0
          ? Array.from(
            new Set(
              creditNoteItems
                .map((item) => item.extendedAttributes?.creditNoteNumber)
                .filter(Boolean)
            )
          )
          : [];

        const paidBy = Array.from(
          new Set(
            nonCreditNoteItems
              .map((item) => item.modeOfPayment)
              .filter(Boolean)
          )
        ).join(", ");

        const paymentDate = firstItem.paymentDate
          ? dayjs(firstItem.paymentDate).format("DD-MM-YYYY")
          : "-";

        const modeGroups = nonCreditNoteItems.reduce((acc, item) => {
          const mode = item.modeOfPayment;
          if (mode) {
            if (!acc[mode]) {
              acc[mode] = {
                shortageAmount: item.extendedAttributes?.shortageAmount || 0,
                paidAmount: 0,
                idList: [],
                status: item.status || "Pending",
              };
            }
            acc[mode].paidAmount += item.collectedAmount || 0;
            acc[mode].idList.push(item.id);
          }
          return acc;
        }, {} as { [key: string]: { shortageAmount: number; paidAmount: number; idList: string[]; status: string } });

        const paymentDetails = Object.keys(modeGroups).map((mode) => ({
          shortageAmount: modeGroups[mode].shortageAmount,
          paymentMethod: mode,
          idList: modeGroups[mode].idList,
          paymentDate: paymentDate,
          paidAmount: modeGroups[mode].paidAmount,
          status: modeGroups[mode].status,
        }));

        const totalShortageAmount = paymentDetails.reduce(
          (sum, detail) => sum + detail.shortageAmount,
          0
        );

        const approvedPaidAmount = group.reduce(
          (sum, item) =>
            sum +
            (item.status?.toUpperCase() === "APPROVED"
              ? item.collectedAmount || 0
              : 0),
          0
        );

        const hasPendingStatus = group.some(
          (item) => item.status?.toUpperCase() === "PENDING"
        );
        const hasApprovedStatus = group.some(
          (item) => item.status?.toUpperCase() === "APPROVED"
        );
        const hasRejectedStatus = group.some(
          (item) => item.status?.toUpperCase() === "REJECTED"
        );

        let paymentApprovalStatus: string;
        if (hasPendingStatus) {
          paymentApprovalStatus = "Pending";
        } else if (hasApprovedStatus) {
          paymentApprovalStatus = "Approved";
        } else if (hasRejectedStatus) {
          paymentApprovalStatus = "Rejected";
        } else {
          paymentApprovalStatus = firstItem.status || "Pending";
        }

        return {
          id: index + 1,
          paymentDate,
          paidBy: paidBy || "-",
          paidAmount: `${truncateToTwoDecimals(sumCollectedAmount)}`,
          invoices,
          creditNoteUsed,
          shortageAmount:
            totalShortageAmount !== 0
              ? `${truncateToTwoDecimals(totalShortageAmount)}`
              : "0.00",
          paymentApprovalStatus,
          approvedPaidAmount: `${truncateToTwoDecimals(approvedPaidAmount)}`,
          paymentDetails,
          creationTime: firstItem.creationTime, // Store creationTime for sorting
          ...(paymentApprovalStatus.toUpperCase() !== "PENDING" && {
            showEyeIcon: true,
          }),
        };
      }
    );

    // Sort tableRows by creationTime in decreasing order
    tableRows.sort((a, b) => {
      const dateA = new Date(a.creationTime);
      const dateB = new Date(b.creationTime);
      return dateB.getTime() - dateA.getTime();
    });

    // Reassign id to maintain sequential order after sorting
    tableRows.forEach((row, index) => {
      row.id = index + 1;
    });

    return tableRows;
  } catch (error) {
    console.error("Error fetching payment collection data:", error);
    throw error;
  }
};


export const fetchPaymentSummary = async (): Promise<PaymentSummary> => {
  try {
    const fromDate = getUTCDate(dayjs(), "start")
    const toDate = getUTCDate(dayjs(), "end")

     const loginId = JSON.parse(localStorage.authContext)?.user?.loginId;

    const response = await ChannelkartNetworkGet(
      `/v1/paymentCollection/paymentSummaryByMode?fromDate=${fromDate}&toDate=${toDate}&supplier=${loginId}`
    );


    if (!response || response.status !== 200) {
      throw new Error(`Some error occured while fetching payment summary`);
    }

    const data = await response?.data?.features;
    const summary: PaymentSummary = {
      paidByCash: 0,
      paidByUpi: 0,
      paidByCheque: 0,
      paidByNeft: 0,
    };

    // Process response data with null checks
    const features = Array.isArray(data) ? data : [];

    features.forEach((item: any) => {
      const modeOfPayment = item?.modeOfPayment;
      const totalAmount =
        typeof item?.totalAmount === "number" ? item.totalAmount : 0;

      if (modeOfPayment) {
        switch (modeOfPayment.toUpperCase()) {
          case "CASH":
            summary.paidByCash = totalAmount;
            break;
          case "UPI":
            summary.paidByUpi = totalAmount;
            break;
          case "CHEQUE":
            summary.paidByCheque = totalAmount;
            break;
          case "NEFT":
            summary.paidByNeft = totalAmount;
            break;
          default:
            break;
        }
      }
    });

    return summary;
  } catch (e) {
    console.error("Error fetching payment summary:", e);
    throw e; // Re-throw to allow caller to handle
  }
};

export const fetchCreditNotes = async (
  outletCode: string
): Promise<CreditNoteData[]> => {
  if (!outletCode) {
    console.error("Outlet code is missing");
    throw new Error("Outlet code is required");
  }

  const loginId = JSON.parse(localStorage.authContext)?.user?.loginId;

  try {
    const response = await ChannelkartNetworkGet(
      `/creditNote/getActiveCreditNotesByOutlet?outletCode=${encodeURIComponent(
        outletCode
      )}&supplier=${loginId}`
    );

    const data = Array.isArray(response?.data?.content) ? response?.data?.content : [];
    const formattedCreditNotes: CreditNoteData[] = data.map((note: any) => ({
      id: note.id ?? "",
      amount: note.amount || 0,
      noteNumber: note.creditNoteNumber ?? "",
      availableAmount: note.availableAmount || 0,
      expired: note.expired || false,
      endDate: note.endDate || "",
    }));

    return formattedCreditNotes;
  } catch (error) {
    console.error("Error fetching credit notes:", error);
    throw error;
  }
};

export const fetchCreditNoteByNumber = async (
  creditNoteNumber: string
): Promise<CreditNoteDetails> => {
  if (!creditNoteNumber) {
    console.error("Credit note number is missing");
    throw new Error("Credit note number is required");
  }

  try {
    const response = await ChannelkartNetworkGet(
      `/creditNote/getCreditNoteByCreditNoteNumber?creditNoteNumber=${encodeURIComponent(creditNoteNumber)}`
    );

    if (!response || typeof response?.data !== "object") {
      throw new Error("Invalid response from API");
    }

    const {
      creditNoteNumber: noteNumber,
      amount,
      issueDate,
      returnId,
      startDate,
      endDate,
    } = response?.data;

    return {
      creditNoteNumber: noteNumber || "",
      amount: typeof amount === "number" ? amount : 0,
      issueDate: issueDate ? dayjs(issueDate).format("DD-MM-YYYY") : "",
      returnId: returnId || "",
      startDate: startDate ? dayjs(startDate).format("DD-MM-YYYY") : "",
      endDate: endDate ? dayjs(endDate).format("DD-MM-YYYY") : "",
    };
  } catch (error) {
    console.error("Error fetching credit note details:", error);
    throw error;
  }
};

export const approvePaymentCollection = async (
  shortageAmount: number,
  paymentMethod: string,
  idList: string[],
  outletType: string
): Promise<void> => {
  try {
    // Validate input parameters
    if (!paymentMethod || !Array.isArray(idList) || idList.length === 0) {
      throw new Error("Invalid input: paymentMethod and idList are required");
    }

    const payload = {
      shortageAmount,
      paymentMethod,
      idList,
      outletType
    };

    const response = await ChannelkartNetworkPut(
      "/v1/paymentCollection/approvePaymentCollection",
      payload
    );

    if (!response || !response.status || response.status !== 200) {
      throw new Error(`HTTP error! status: ${response?.status || "unknown"}`);
    }

    console.log("Payment approved successfully");
  } catch (error) {
    console.error("Error approving payment collection:", error);
    throw error;
  }
};

export const rejectPaymentCollection = async (
  paymentMethod: string,
  idList: string[],
  outletType: string
): Promise<void> => {
  try {
    // Validate input parameters
    if (!paymentMethod || !Array.isArray(idList) || idList.length === 0) {
      throw new Error("Invalid input: paymentMethod and idList are required");
    }

    const payload = {
      paymentMethod,
      idList,
      outletType
    };

    const response = await ChannelkartNetworkPut(
      "/v1/paymentCollection/rejectPaymentCollection",
      payload
    );

    if (!response || !response.status || response.status !== 200) {
      throw new Error(`HTTP error! status: ${response?.status || "unknown"}`);
    }

    console.log("Payment rejected successfully");
  } catch (error) {
    console.error("Error rejecting payment collection:", error);
    throw error;
  }
};



export const getUnpaidInvoicesByOutlet = async (
  outletCode: string
): Promise<InvoiceData[]> => {
  if (!outletCode) {
    console.error("Outlet code is missing");
    throw new Error("Outlet code is required");
  }

  const loginId = JSON.parse(localStorage.authContext)?.user?.loginId;

  try {
    const response = await ChannelkartNetworkGet(
      `/v1/invoiceCollection/getUnpaidInvoicesByOutletCode?outletCode=${encodeURIComponent(
        outletCode
      )}&supplier=${loginId}`
    );

    const data = Array.isArray(response?.data?.content)
      ? response?.data?.content
      : [];

    const formattedInvoices: InvoiceData[] = data.map((invoice: any) => ({
      invoiceNumber: invoice.invoiceNumber?.trim() ?? "",
      status: invoice.status ?? "",
      pendingAmount: invoice.pendingAmount || 0
    }));

    return formattedInvoices;
  } catch (error) {
    console.error("Error fetching unpaid invoices:", error);
    throw error;
  }
};
